import { useState } from "react";
import { Phone, Mail, Upload, CheckCircle, Send } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { useToast } from "../hooks/use-toast";

export const ServiceRequestPage = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    isCurrentCustomer: false,
    requestDetails: "",
    contactMethod: "email",
  });
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (checked) => {
    setFormData((prev) => ({ ...prev, isCurrentCustomer: checked }));
  };

  const handleContactMethodChange = (value) => {
    setFormData((prev) => ({ ...prev, contactMethod: value }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = [];

    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: `${file.name} is larger than 10MB`,
          variant: "destructive",
        });
        continue;
      }
      validFiles.push(file);
    }

    setAttachedFiles((prev) => [...prev, ...validFiles]);
  };

  const removeFile = (index) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.name || !formData.phone || !formData.email || !formData.requestDetails) {
      toast({
        title: "Required fields missing",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // TODO: Backend integration - This will be implemented in the backend phase
      await new Promise((resolve) => setTimeout(resolve, 1500));

      toast({
        title: "Service request submitted!",
        description: `We'll contact you via ${formData.contactMethod} when your request is completed.`,
      });

      // Reset form
      setFormData({
        name: "",
        phone: "",
        email: "",
        isCurrentCustomer: false,
        requestDetails: "",
        contactMethod: "email",
      });
      setAttachedFiles([]);
      if (document.getElementById("file-upload")) {
        document.getElementById("file-upload").value = "";
      }
    } catch (error) {
      toast({
        title: "Submission failed",
        description: "Please try again or contact us directly",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary to-blue-700 text-white py-16 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.pexels.com/photos/8441820/pexels-photo-8441820.jpeg?w=1600&q=80')",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary/95 to-blue-700/90"></div>
        </div>
        <div className="container-custom relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Submit a Service Request</h1>
            <p className="text-xl text-blue-100">
              Need assistance with your policy or have a question? Submit your request and we'll get back to you promptly.
            </p>
          </div>
        </div>
      </section>

      {/* Service Request Form */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <Card className="shadow-xl border-2 border-primary/20">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-slate-50">
                <CardTitle className="text-3xl text-center text-foreground">
                  Service Request Form
                </CardTitle>
                <p className="text-center text-muted-foreground mt-2">
                  Fill out the form below and we'll respond according to your preferred contact method
                </p>
              </CardHeader>
              <CardContent className="pt-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name */}
                  <div>
                    <Label htmlFor="name" className="text-base">
                      Full Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="John Doe"
                      required
                      className="mt-2"
                    />
                  </div>

                  {/* Phone and Email */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="phone" className="text-base">
                        Phone Number <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="(123) 456-7890"
                        required
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label htmlFor="email" className="text-base">
                        Email Address <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="john@example.com"
                        required
                        className="mt-2"
                      />
                    </div>
                  </div>

                  {/* Current Customer Checkbox */}
                  <div className="flex items-center space-x-3 p-4 bg-blue-50 rounded-lg">
                    <Checkbox
                      id="currentCustomer"
                      checked={formData.isCurrentCustomer}
                      onCheckedChange={handleCheckboxChange}
                    />
                    <Label
                      htmlFor="currentCustomer"
                      className="text-base font-normal cursor-pointer"
                    >
                      I am a current customer
                    </Label>
                  </div>

                  {/* Request Details */}
                  <div>
                    <Label htmlFor="requestDetails" className="text-base">
                      Request Details <span className="text-red-500">*</span>
                    </Label>
                    <p className="text-sm text-muted-foreground mb-2">
                      Please provide detailed information about your service request
                    </p>
                    <Textarea
                      id="requestDetails"
                      name="requestDetails"
                      value={formData.requestDetails}
                      onChange={handleInputChange}
                      placeholder="Describe your service request in detail..."
                      rows={6}
                      required
                      className="mt-2"
                    />
                  </div>

                  {/* File Upload */}
                  <div>
                    <Label htmlFor="file-upload" className="text-base">
                      Attach Documents (Optional)
                    </Label>
                    <p className="text-sm text-muted-foreground mb-2">
                      Upload any relevant documents to support your request
                    </p>
                    <div className="mt-2">
                      <label
                        htmlFor="file-upload"
                        className="flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary transition-colors bg-gray-50 hover:bg-gray-100"
                      >
                        <Upload className="h-5 w-5 text-muted-foreground mr-2" />
                        <span className="text-sm text-muted-foreground">
                          Click to upload files
                        </span>
                      </label>
                      <input
                        id="file-upload"
                        type="file"
                        onChange={handleFileChange}
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                        multiple
                        className="hidden"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Accepted formats: PDF, JPG, PNG, DOC, DOCX (Max 10MB per file)
                    </p>

                    {/* Display attached files */}
                    {attachedFiles.length > 0 && (
                      <div className="mt-4 space-y-2">
                        {attachedFiles.map((file, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg"
                          >
                            <div className="flex items-center space-x-2">
                              <CheckCircle className="h-4 w-4 text-green-500" />
                              <span className="text-sm text-foreground">{file.name}</span>
                              <span className="text-xs text-muted-foreground">
                                ({(file.size / 1024).toFixed(1)} KB)
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeFile(index)}
                              className="text-red-500 hover:text-red-700 text-sm font-medium"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Contact Method */}
                  <div>
                    <Label className="text-base mb-3 block">
                      How would you like to be contacted when your request is completed?
                    </Label>
                    <RadioGroup
                      value={formData.contactMethod}
                      onValueChange={handleContactMethodChange}
                      className="space-y-3"
                    >
                      <div className="flex items-center space-x-3 p-3 border-2 rounded-lg hover:border-primary transition-colors">
                        <RadioGroupItem value="email" id="email-contact" />
                        <Label htmlFor="email-contact" className="flex items-center cursor-pointer font-normal flex-1">
                          <Mail className="h-5 w-5 text-primary mr-2" />
                          Email me
                        </Label>
                      </div>
                      <div className="flex items-center space-x-3 p-3 border-2 rounded-lg hover:border-primary transition-colors">
                        <RadioGroupItem value="phone" id="phone-contact" />
                        <Label htmlFor="phone-contact" className="flex items-center cursor-pointer font-normal flex-1">
                          <Phone className="h-5 w-5 text-primary mr-2" />
                          Call me
                        </Label>
                      </div>
                      <div className="flex items-center space-x-3 p-3 border-2 rounded-lg hover:border-primary transition-colors">
                        <RadioGroupItem value="both" id="both-contact" />
                        <Label htmlFor="both-contact" className="flex items-center cursor-pointer font-normal flex-1">
                          <Mail className="h-5 w-5 text-primary mr-2" />
                          Either email or phone is fine
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    size="lg"
                    className="w-full font-semibold"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>Processing...</>
                    ) : (
                      <>
                        Submit Service Request
                        <Send className="ml-2 h-5 w-5" />
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-center text-muted-foreground">
                    We typically respond to service requests within 1-2 business days
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Google Review Section */}
      <section className="py-12 bg-gradient-to-r from-blue-500 to-blue-600">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <div className="mb-6">
              <div className="flex justify-center mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <svg
                    key={star}
                    className="w-8 h-8 text-yellow-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Love Our Service? Share Your Experience!
              </h2>
              <p className="text-xl text-blue-100 mb-6">
                Your feedback helps us serve you better and helps others find quality insurance protection
              </p>
            </div>
            <a
              href="https://share.google/s3cGtVTEHM4Fhmzsd"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                size="lg"
                variant="secondary"
                className="bg-white text-primary hover:bg-gray-100 font-semibold px-8 py-6 text-lg"
              >
                <svg
                  className="w-6 h-6 mr-2"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Leave Us a Google Review
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
